// Single source of truth: auth + elections + selection + locale.
// FlowState is DERIVED from this store in one place (deriveFlowState) — the
// FlowGate is the only thing that redirects. This replaces the Flutter
// go_router redirect that raced 4 async providers.

import { create } from "zustand";
import {
  ApiError,
  type Election,
  type Voter,
} from "@/types";
import * as api from "@/lib/api";
import { clearToken, getToken } from "@/lib/token";
import { type Locale, detectLocale } from "@/lib/i18n";

// ---- Flow state machine ----
export type FlowState =
  | "checkingAuth" // boot: restoring session
  | "loggedOut" // no session -> /login
  | "authing" // sending OTP / verifying -> stay on /login
  | "checkingElections" // authed, loading /api/elections
  | "needsPrevalidation" // authed, zero enabled elections -> /prevalidation
  | "readyToVote" // has enabled election(s), none selected yet -> /start-voting
  | "voting" // active election loaded, not voted -> /candidates
  | "confirming" // reviewing selection -> /confirmation (user-driven)
  | "voted" // active election has_voted -> /success
  | "voteEnded"; // active election ended / not open / none -> /vote-ended

export type AuthStatus =
  | "initial"
  | "loggedOut"
  | "sendingOtp"
  | "otpSent"
  | "authenticating" // exchanging token with backend
  | "authenticated"
  | "error";

type LoadStatus = "idle" | "loading" | "loaded" | "error" | "noElection";

interface AppState {
  locale: Locale;

  auth: {
    status: AuthStatus;
    voter: Voter | null;
    verificationId: string | null; // native flow; web uses confirmationResult in auth module
    error: string | null;
  };

  // Elections the voter is enabled in (from /api/elections).
  available: { status: LoadStatus; list: Election[] };

  // The election currently being voted on.
  active: { status: LoadStatus; election: Election | null; error: string | null };

  // election_id from URL, preserved across the flow (also mirrored to localStorage).
  electionId: string | null;

  // Selected candidate ids for the active election.
  selection: string[];

  // Whether the user is mid-confirmation review (drives 'confirming' vs 'voting').
  reviewing: boolean;

  // ---- locale ----
  setLocale: (l: Locale) => void;
  initLocale: () => void;

  // ---- auth ----
  setAuthStatus: (s: AuthStatus) => void;
  setVoter: (v: Voter) => void;
  setAuthError: (msg: string) => void;
  setVerificationId: (id: string | null) => void;
  restoreSession: () => Promise<void>; // boot: token -> /voters/me
  refreshSession: () => Promise<void>; // 401 recovery: mint a fresh bearer
  bootstrap: (p: { phone?: string; magicToken?: string }) => Promise<void>;
  impersonateVoter: (phone: string, magicToken: string) => Promise<void>;
  signOut: () => Promise<void>;

  // ---- elections ----
  setElectionId: (id: string | null) => void;
  loadAvailableElections: () => Promise<void>;
  loadActiveElection: () => Promise<void>; // by electionId if set
  resetActive: () => void; // back to election picker: clear active + selection + electionId

  // ---- selection ----
  toggleCandidate: (id: string) => "selected" | "deselected" | "atMax";
  restoreSelection: () => void;
  clearSelection: () => void;
  setReviewing: (b: boolean) => void;

  // ---- vote result ----
  markVoted: () => void;
}

// ---- persistence ----
// localStorage (not sessionStorage): kept in step with lib/token.ts. Voters use
// their own phones and sessionStorage is per-tab, so a re-scanned QR or a tab
// discarded by iOS threw away the chosen election and a half-made ballot along
// with the session. signOut() still wipes all of it.
// There is deliberately NO local "has voted" cache — the API `has_voted` field
// is the SOLE source of truth, so a deleted/reset vote can never trap a voter
// on /success (the Flutter app took the same API-only stance).
const selKey = (electionId: string) => `selection_${electionId}`;
const ELECTION_ID_KEY = "election_id";

function ss(): Storage | null {
  return typeof window !== "undefined" ? window.localStorage : null;
}

// Drops the previous voter's ballots without touching which election is being
// voted — ClientBoot persists election_id BEFORE bootstrap runs, so wiping it
// here would lose the election a magic link pointed at.
function clearStoredSelections() {
  const s = ss();
  if (!s) return;
  for (let i = s.length - 1; i >= 0; i--) {
    const k = s.key(i);
    if (k && k.startsWith("selection_")) s.removeItem(k);
  }
}

function clearVotingStorage() {
  const s = ss();
  if (!s) return;
  s.removeItem(ELECTION_ID_KEY);
  clearStoredSelections();
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

// Firebase rehydrates currentUser asynchronously from IndexedDB, so reading it
// straight after a reload returns null even for a voter who IS signed in. That
// null was being read as "session over". Wait for the SDK to settle instead.
async function firebaseUser(timeoutMs = 4000) {
  const { firebaseAuth } = await import("@/lib/firebase");
  const auth = firebaseAuth();
  if (auth.currentUser) return auth.currentUser;
  const { onAuthStateChanged } = await import("firebase/auth");
  return new Promise<import("firebase/auth").User | null>((resolve) => {
    const timer = setTimeout(() => {
      off();
      resolve(null);
    }, timeoutMs);
    const off = onAuthStateChanged(auth, (u) => {
      if (!u) return; // a null tick is "not ready yet", not "signed out"
      clearTimeout(timer);
      off();
      resolve(u);
    });
  });
}

// The Firebase sign-in lasts for months; only the backend bearer ages out. So a
// dead bearer is not a dead voter — mint a new one from Firebase rather than
// sending them back to the SMS code. Returns null only if Firebase itself is
// gone (or the voter arrived by magic link and never signed into Firebase).
async function reloginFromFirebase(): Promise<Voter | null> {
  const user = await firebaseUser();
  if (!user) return null;
  try {
    return await api.login(await user.getIdToken(true));
  } catch {
    return null;
  }
}

// Single-flight guard for refreshSession. Several requests can 401 at once
// (loadAvailableElections + loadActiveElection on a flaky connection), and
// without coalescing they interleave destructively: the first refresh fails and
// signs out — clearing storage, Echo and Firebase — while the second succeeds a
// moment later and writes "authenticated" back over it, leaving a session that
// looks live but has no bearer and no socket.
let refreshInFlight: Promise<void> | null = null;

export const useApp = create<AppState>((set, get) => ({
  locale: "ro",
  auth: { status: "initial", voter: null, verificationId: null, error: null },
  available: { status: "idle", list: [] },
  active: { status: "idle", election: null, error: null },
  electionId: null,
  selection: [],
  reviewing: false,

  setLocale: (l) => {
    if (typeof window !== "undefined") window.localStorage.setItem("locale", l);
    set({ locale: l });
  },
  initLocale: () => set({ locale: detectLocale() }),

  setAuthStatus: (status) =>
    set((s) => ({ auth: { ...s.auth, status, error: null } })),
  setVoter: (voter) =>
    set((s) => ({ auth: { ...s.auth, voter, status: "authenticated", error: null } })),
  setAuthError: (error) =>
    set((s) => ({ auth: { ...s.auth, status: "error", error } })),
  setVerificationId: (verificationId) =>
    set((s) => ({ auth: { ...s.auth, verificationId } })),

  restoreSession: async () => {
    if (!getToken()) {
      set((s) => ({ auth: { ...s.auth, status: "loggedOut" } }));
      return;
    }
    // Retry before giving up, so a flaky election-day connection — or a backend
    // that just restarted — doesn't bounce a valid voter to /login. Even a 401
    // gets the full three attempts: see the auth branch below.
    //
    // Asking Firebase for a user waits on the SDK rehydrating from IndexedDB,
    // and a voter who arrived by magic link has no Firebase session at all. One
    // "no user" answer settles it for this whole call: repeating it per attempt
    // spent 4s each time doing nothing but delaying the login screen.
    let firebaseAsked = false;
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const voter = await api.getMe();
        set((s) => ({
          auth: { ...s.auth, voter, status: "authenticated", error: null },
        }));
        return;
      } catch (e) {
        if (e instanceof ApiError && e.code === "auth") {
          // Expired bearer + live Firebase session = renew it silently. Only a
          // voter Firebase no longer knows is really logged out.
          if (!firebaseAsked) {
            firebaseAsked = true;
            const voter = await reloginFromFirebase();
            if (voter) {
              set((s) => ({
                auth: { ...s.auth, voter, status: "authenticated", error: null },
              }));
              return;
            }
          }
          // Do NOT treat one 401 as proof. A backend restarted mid-election
          // answers 401 for a few seconds, and throwing the voter to /login
          // there costs them an SMS and their place in the queue. Give the
          // bearer the remaining attempts before declaring it dead.
          if (attempt < 2) {
            await sleep(1500);
            continue;
          }
          clearToken();
          set((s) => ({ auth: { ...s.auth, status: "loggedOut", voter: null } }));
          return;
        }
        // api.request already re-sent this GET three times before throwing a
        // network error, so looping here only multiplies the wait: nine
        // requests at a 30s timeout each is minutes of spinner before the
        // voter is even told they are offline. A server error is different —
        // that one WAS a response, nothing retried it, and a backend restart
        // recovers in seconds.
        if (e instanceof ApiError && e.code === "network") break;
        if (attempt < 2) await sleep(1500);
      }
    }
    // Persistent transient failure: keep the token (session may still be valid),
    // surface a retriable error rather than pretending the voter is logged out.
    set((s) => ({
      // A real sentence, not a sentinel: the splash renders this through
      // localizeError, and "network" alone matches no rule.
      auth: { ...s.auth, status: "error", error: "No internet connection" },
    }));
  },

  // A 401 on a normal call means the BACKEND bearer aged out, not that the voter
  // is gone: the Firebase session usually still mints a fresh one. Signing out
  // here would wipe clearVotingStorage() — the half-finished ballot included —
  // so recover the token instead and only give up when Firebase itself is dead.
  // Mirrors auth_providers.dart refreshToken(); api.login lives on an auth path,
  // so a 401 raised in here cannot re-enter this handler.
  refreshSession: async () => {
    if (get().auth.status !== "authenticated") return;
    if (refreshInFlight) return refreshInFlight;
    refreshInFlight = (async () => {
    try {
      const user = await firebaseUser();
      // No Firebase user is NOT proof the session is dead, and treating it as
      // such logged real voters out:
      //   - impersonateVoter() gets its bearer straight from the backend and
      //     never signs into Firebase, so a magic-link voter has no currentUser
      //     for their whole session;
      //   - Firebase rehydrates currentUser asynchronously from IndexedDB, and
      //     visibilitychange fires on page show, so after a reload this reads
      //     null before the SDK has caught up.
      // Nothing to refresh from, so leave the bearer alone — the next real
      // request is what decides whether it is still good.
      if (!user) return;
      const voter = await api.login(await user.getIdToken(true));
      set((s) => ({
        auth: { ...s.auth, voter, status: "authenticated", error: null },
      }));
    } catch (e) {
      // Never sign the voter out from here. This ran on every visibilitychange
      // and on every 401, so one bad response — a backend restart, a token the
      // backend momentarily refused — threw a voter out mid-election with their
      // ballot. Keep the session: restoreSession() can still renew it from
      // Firebase, and only an explicit sign-out ends a session now.
      console.error("[refreshSession] keeping session despite", e);
    }
    })().finally(() => {
      refreshInFlight = null;
    });
    return refreshInFlight;
  },

  // One-shot boot: impersonation via URL params wins, else restore session.
  bootstrap: async ({ phone, magicToken }) => {
    if (phone && magicToken) {
      // A magic link names ONE voter, so it outranks whatever session this tab
      // still holds. Deferring to a leftover token silently signs the previous
      // voter back in on a reused kiosk tab and burns the link.
      clearToken();
      clearStoredSelections();
      await get().impersonateVoter(phone, magicToken);
      return;
    }
    await get().restoreSession();
  },

  impersonateVoter: async (phone, magicToken) => {
    set((s) => ({ auth: { ...s.auth, status: "authenticating", error: null } }));
    try {
      const voter = await api.impersonate(phone, magicToken);
      set((s) => ({ auth: { ...s.auth, voter, status: "authenticated" } }));
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : "Impersonation failed";
      set((s) => ({ auth: { ...s.auth, status: "loggedOut", error: msg } }));
    }
  },

  signOut: async () => {
    clearToken();
    clearVotingStorage();
    try {
      const { disconnectEcho } = await import("@/lib/echo");
      disconnectEcho(); // drop the WS so a re-login rebuilds it with a fresh token
    } catch {
      /* echo may never have been created */
    }
    try {
      const { signOut } = await import("firebase/auth");
      const { firebaseAuth } = await import("@/lib/firebase");
      await signOut(firebaseAuth());
    } catch {
      // ignore — token already cleared
    }
    set({
      auth: { status: "loggedOut", voter: null, verificationId: null, error: null },
      available: { status: "idle", list: [] },
      active: { status: "idle", election: null, error: null },
      electionId: null,
      selection: [],
      reviewing: false,
    });
  },

  setElectionId: (electionId) => {
    const s = ss();
    if (s) {
      if (electionId) s.setItem(ELECTION_ID_KEY, electionId);
      else s.removeItem(ELECTION_ID_KEY);
    }
    set({ electionId });
  },

  loadAvailableElections: async () => {
    set((s) => ({ available: { ...s.available, status: "loading" } }));
    try {
      const list = await api.getElections();
      set({ available: { status: "loaded", list } });
    } catch {
      // A failed REFRESH must not discard a list we already have: deriveFlowState
      // routes `error` to /prevalidation, so one blip on the manual refresh button
      // would throw a voter off the picker back to the QR screen. Only a failed
      // FIRST load is a real error — after that, keep showing what we had.
      set((s) =>
        s.available.list.length > 0
          ? { available: { status: "loaded", list: s.available.list } }
          : { available: { status: "error", list: [] } },
      );
    }
  },

  loadActiveElection: async () => {
    const id = get().electionId;
    set((s) => ({ active: { ...s.active, status: "loading", error: null } }));
    try {
      const election = id
        ? await api.getElectionById(id)
        : (await api.getElections())[0];
      if (!election) {
        set({ active: { status: "noElection", election: null, error: null } });
        return;
      }
      // Only an "ongoing" election is votable. "ended" and "upcoming" both route
      // to the vote-ended screen so a magic link to a not-yet-open election can't
      // drop the voter onto the candidate picker.
      const status: LoadStatus =
        election.status === "ongoing" ? "loaded" : "noElection";
      set({ active: { status, election, error: null } });
      if (status === "loaded") get().restoreSelection();
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : "Failed to load election";
      set({ active: { status: "error", election: null, error: msg } });
    }
  },

  resetActive: () => {
    const s = ss();
    if (s) s.removeItem(ELECTION_ID_KEY);
    set({
      active: { status: "idle", election: null, error: null },
      selection: [],
      reviewing: false,
      electionId: null,
    });
  },

  toggleCandidate: (id) => {
    const { selection, active } = get();
    const max = active.election?.requiredVotesCount ?? 0;
    let result: "selected" | "deselected" | "atMax";
    let next: string[];
    if (selection.includes(id)) {
      next = selection.filter((x) => x !== id);
      result = "deselected";
    } else if (selection.length >= max) {
      return "atMax";
    } else {
      next = [...selection, id];
      result = "selected";
    }
    set({ selection: next });
    const eid = active.election?.id;
    const s = ss();
    if (eid && s) s.setItem(selKey(eid), JSON.stringify(next));
    return result;
  },

  restoreSelection: () => {
    const eid = get().active.election?.id;
    const s = ss();
    if (!eid || !s) return;
    const raw = s.getItem(selKey(eid));
    if (raw) {
      try {
        set({ selection: JSON.parse(raw) as string[] });
      } catch {
        /* ignore */
      }
    }
  },

  clearSelection: () => {
    const eid = get().active.election?.id;
    const s = ss();
    if (eid && s) s.removeItem(selKey(eid));
    set({ selection: [] });
  },

  setReviewing: (reviewing) => set({ reviewing }),

  markVoted: () => {
    const el = get().active.election;
    if (!el) return;
    const s = ss();
    if (s) s.removeItem(selKey(el.id)); // vote cast — drop the saved selection
    set((state) => ({
      active: {
        ...state.active,
        election: state.active.election
          ? { ...state.active.election, hasVoted: true }
          : null,
      },
      // Keep the picker list in sync too: /start-voting renders from
      // available.list, so without this the just-voted election still shows as
      // votable (and tappable) until the next /api/elections fetch.
      available: {
        ...state.available,
        list: state.available.list.map((e) =>
          e.id === el.id ? { ...e, hasVoted: true } : e,
        ),
      },
      selection: [],
      reviewing: false,
    }));
  },
}));

// ---- Flow derivation (the ONE place routing decisions come from) ----
// Pure + colocated with the store: a single, testable mapping state -> screen.
export function deriveFlowState(s: AppState): FlowState {
  const a = s.auth;
  if (a.status === "initial") return "checkingAuth";
  if (a.status === "error") return "checkingAuth"; // transient restore failure: hold on splash, don't claim logged-out
  if (a.status !== "authenticated") {
    if (a.status === "sendingOtp" || a.status === "otpSent" || a.status === "authenticating")
      return "authing";
    return "loggedOut";
  }

  // authenticated from here
  const active = s.active;
  if (active.status === "loaded" && active.election) {
    const el = active.election;
    // API is the SOLE source of truth for hasVoted (no local cache override).
    if (el.hasVoted) return "voted";
    if (el.status !== "ongoing") return "voteEnded";
    return s.reviewing ? "confirming" : "voting";
  }
  if (active.status === "noElection") return "voteEnded";

  // An election is chosen and mid-load — hold on the splash, don't bounce back
  // to the picker while the request is in flight.
  if (s.electionId && (active.status === "loading" || active.status === "idle")) {
    return "checkingElections";
  }

  // no active election resolved yet — decide via available elections
  const av = s.available;
  if (av.status === "loaded") {
    if (av.list.length === 0) return "needsPrevalidation";
    return "readyToVote";
  }
  // A failed elections fetch must not strand the voter on a dead spinner:
  // fall back to prevalidation (recoverable — they can re-scan the QR), matching
  // the Flutter default branch.
  if (av.status === "error") return "needsPrevalidation";
  return "checkingElections";
}
